import { useEffect, useMemo, useRef, useState } from "react";

import type { SlotMetrics } from "../modules/teleop-session";
import type { CameraVideoStats, CameraFrameTiming } from "../modules/teleop-state";
import type { VideoClockEstimate } from "../modules/teleop-state";
import { useVideoFramePath } from "../hooks/useVideoFramePath";
import { VideoFramePathPanel } from "./VideoFramePathPanel";
import { StatusBar } from "./StatusBar";
import { VideoLatencyPanel, type VideoLatencyActions } from "./VideoLatencyPanel";
import styles from "./VideoGrid.module.scss";

export type CameraToggleHandler = (
  mount: string,
  slot: number,
  enabled: boolean
) => Promise<boolean | void> | boolean | void;

interface VideoCardProps extends VideoLatencyActions {
  slot: number;
  mount: string;
  title: string;
  stream: MediaStream | null;
  metrics: SlotMetrics | undefined;
  backendStats: CameraVideoStats | undefined;
  backendClock?: VideoClockEstimate | null;
  requestFrameTiming?: (mount: string) => void;
  readFrameTiming?: (mount: string) => CameraFrameTiming | undefined;
  overlayHidden: boolean;
  cameraOn: boolean;
  onCameraToggle: CameraToggleHandler | undefined;
  toggleDisabled?: boolean;
  onCameraExpand?: (mount: string) => void;
  requestKeyframes?: (slot: number) => void;
  /** Called on every decoded frame; monitor mode uses this as a motion safety gate. */
  onDecodedFrame?: (slot: number) => void;
  /** ViewerPage's fullscreen layout — the card fills its grid cell edge-to-edge
   * (object-fit: contain, no intrinsic aspect-ratio box) instead of the docked view's
   * content-sized card, and the header floats over the picture instead of pushing
   * it down. */
  fullscreen?: boolean;
  /** Fullscreen only — fades the floating header out after a run of inactivity (see
   * ViewerPage); ignored while `fullscreen` is false, where the header is always shown. */
  chromeVisible?: boolean;
  /** Docked "fit" layout — the picture is letterboxed into whatever height the grid cell
   * gets instead of sizing the card to the feed's aspect ratio (see VideoGrid `fit`). */
  fit?: boolean;
  presentationHidden?: boolean;
}

interface VideoDetails {
  size: string;
  fps: string;
  decodedFps: string;
  bitrate: string;
  drop: number;
  queue: string;
  oldestWait: string;
  queueWait: string;
  jitterBuffer: string;
  decode: string;
  backendStatus: string;
}

const EMPTY_DETAILS: VideoDetails = {
  size: "—", fps: "0 fps", decodedFps:"—", bitrate: "— kbit/s", drop: 0,
  queue: "—", oldestWait: "—", queueWait: "—", jitterBuffer: "—", decode: "—",
  backendStatus: "Ожидание данных backend",
};

function milliseconds(value: number | null | undefined): string {
  return typeof value === "number" && Number.isFinite(value) && value >= 0 ? `${value.toFixed(1)} мс` : "—";
}

/**
 * Owns a single <video> element: attaches the remote stream, controls play/pause via
 * the overlay state, counts FPS, detects stalls (requesting keyframes) and renders a
 * plain-language signal badge. The raw numbers behind it (resolution/fps/bitrate/drop)
 * only show up in a click-opened details panel — see the info button in the header.
 * All DOM/render concerns live here — in the React component.
 */
function VideoCard({
  slot,
  mount,
  title,
  stream,
  metrics,
  backendStats,
  backendClock,
  requestFrameTiming,
  readFrameTiming,
  overlayHidden,
  cameraOn,
  onCameraToggle,
  toggleDisabled,
  onCameraExpand,
  requestKeyframes,
  onDecodedFrame,
  setLatencyMode,
  startLatencyComparison,
  cancelLatencyComparison,
  fullscreen = false,
  chromeVisible = true,
  fit = false,
  presentationHidden = false,
}: VideoCardProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const frameCountRef = useRef(0);
  const lastFramesRef = useRef(0);
  const stallTicksRef = useRef(0);
  const metricsRef = useRef<SlotMetrics | undefined>(metrics);
  metricsRef.current = metrics;
  const backendStatsRef = useRef(backendStats);
  backendStatsRef.current = backendStats;
  const [details, setDetails] = useState<VideoDetails>(EMPTY_DETAILS);
  const [signalLive, setSignalLive] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  // The feed's own width/height ratio, once known — drives the card's shape (see
  // `.body`'s inline style below) instead of stretching the video to whatever the
  // grid row happens to be. Sticks at the last known value across an on/off toggle
  // (not reset to null) so the card doesn't resize away and back on every click.
  const [ratio, setRatio] = useState<number | null>(null);
  const cardRef = useRef<HTMLDivElement | null>(null);
  const framePath = useVideoFramePath(videoRef,stream,mount,readFrameTiming,backendClock,metrics,presentationHidden);
  const onFramePath = framePath.onFrame;
  const lastPresentedRef = useRef<number | null>(null);
  const lastFpsTickRef = useRef<number>(performance.now());

  // Track the real feed shape from the <video> element itself — `loadedmetadata` for
  // the initial size, `resize` for a mid-session change (e.g. an ABR resolution step).
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    const updateRatio = () => {
      if (el.videoWidth > 0 && el.videoHeight > 0) setRatio(el.videoWidth / el.videoHeight);
    };
    updateRatio();
    el.addEventListener("loadedmetadata", updateRatio);
    el.addEventListener("resize", updateRatio);
    return () => {
      el.removeEventListener("loadedmetadata", updateRatio);
      el.removeEventListener("resize", updateRatio);
    };
  }, []);

  // Close the details panel on an outside click or Escape — only while it's open, so
  // idle cards (the common case) don't each carry a live document-level listener.
  useEffect(() => {
    if (!detailsOpen) return undefined;
    const onDocClick = (e: MouseEvent) => {
      if (!cardRef.current?.contains(e.target as Node)) setDetailsOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setDetailsOpen(false);
    };
    document.addEventListener("click", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("click", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [detailsOpen]);

  // A camera that's off has nothing to show details about; don't leave the panel
  // stuck open into the next time it turns on.
  useEffect(() => {
    if (!cameraOn) setDetailsOpen(false);
  }, [cameraOn]);

  // Attach the stream and play/pause based on camera state.
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    el.srcObject = stream ?? null;
    if (stream && overlayHidden) {
      void el.play().catch(() => {});
    } else {
      el.pause();
    }
  }, [stream, overlayHidden]);

  // FPS counting + stall detection (reads the video element this card owns).
  useEffect(() => {
    const el = videoRef.current;
    let rafId: number | null = null;
    let fallbackId: ReturnType<typeof setInterval> | null = null;
    let cancelled = false;
    lastPresentedRef.current = null;
    lastFramesRef.current = frameCountRef.current;
    lastFpsTickRef.current = performance.now();
    if (el && typeof el.requestVideoFrameCallback === "function") {
      const onFrame: VideoFrameRequestCallback = (now, metadata) => {
        if (cancelled) return;
        const previous = lastPresentedRef.current;
        frameCountRef.current += previous===null || metadata.presentedFrames<previous ? 1 : metadata.presentedFrames-previous;
        lastPresentedRef.current=metadata.presentedFrames;
        onFramePath(now,metadata);
        onDecodedFrame?.(slot);
        rafId = el.requestVideoFrameCallback(onFrame);
      };
      rafId = el.requestVideoFrameCallback(onFrame);
    }
    if (el && (presentationHidden || typeof el.requestVideoFrameCallback !== "function")) {
      // Hidden tiles may stop compositor callbacks. Count decoded frames so the
      // primary camera's monitor heartbeat still checks fresh video while another
      // camera is expanded. Older browsers fall back to playback progress.
      let lastDecoded = el.getVideoPlaybackQuality?.().totalVideoFrames ?? 0;
      let lastTime = el.currentTime;
      fallbackId = setInterval(() => {
        if (el.paused || el.readyState < 2 || el.videoWidth === 0 || el.videoHeight === 0) return;
        const currentTime = el.currentTime;
        const decoded = presentationHidden
          ? el.getVideoPlaybackQuality?.().totalVideoFrames
          : undefined;
        const progressed = decoded == null ? currentTime > lastTime + 0.001 : decoded > lastDecoded;
        if (progressed) onDecodedFrame?.(slot);
        lastDecoded = decoded ?? lastDecoded;
        lastTime = currentTime;
      }, 100);
    }

    const tick = () => {
      const v = videoRef.current;
      if (!v) return;
      const frames = frameCountRef.current;
      const tickNow=performance.now(),elapsed=tickNow-lastFpsTickRef.current;
      const fps = Math.max(0, Math.round((frames - lastFramesRef.current)*1000/Math.max(1,elapsed)));
      lastFramesRef.current = frames;
      lastFpsTickRef.current=tickNow;
      const hasFrameCallback = typeof v.requestVideoFrameCallback === "function";

      // No new frames + no picture + low readyState → likely a stalled decode. Gated on
      // `cameraOn` (synced across operators via the real stream, see the toggle) rather
      // than the pipeline's local `stateKey`, so this operator doesn't spam keyframe
      // requests for a camera another operator turned off just because their own local
      // read hasn't caught up yet.
      if (fps === 0 && v.videoWidth === 0 && v.readyState < 2 && cameraOn) {
        stallTicksRef.current += 1;
        if (stallTicksRef.current >= 2) {
          stallTicksRef.current = 0;
          requestKeyframes?.(slot);
        }
      } else {
        stallTicksRef.current = 0;
      }

      const w = v.videoWidth;
      const h = v.videoHeight;
      const size = w > 0 && h > 0 ? `${w}×${h}` : "—";
      const fpsText = fps > 0 ? `~${fps} fps` : "0 fps";
      const m = metricsRef.current;
      const bitrate = m && m.bitrateKbps > 0 ? `~${m.bitrateKbps} kbit/s` : "— kbit/s";
      const drop = m?.dropped ?? 0;
      const now = Date.now();
      const b = backendStatsRef.current;
      const backendFresh = b && now - b.receivedAt >= 0 && now - b.receivedAt < 3500;
      const backendLive = backendFresh && b.active;
      const browserFresh = m?.browserStatsAt && now - m.browserStatsAt >= 0 && now - m.browserStatsAt < 3500;
      setDetails({
        size, fps: fpsText, bitrate, drop,
        decodedFps: browserFresh && typeof m.decodedFps === "number" ? `~${m.decodedFps.toFixed(1)} fps` : "—",
        queue: backendLive && b.queueItems !== null
          ? `${b.queueItems} ${b.queueUnit === "packets" ? "пак." : "кадр."}` : "—",
        oldestWait: milliseconds(backendLive ? b.oldestWaitMs : null),
        queueWait: backendLive ? `${milliseconds(b.meanWaitMs)} / ${milliseconds(b.maxWaitMs)}` : "—",
        jitterBuffer: milliseconds(browserFresh ? m.jitterBufferMs : null),
        decode: milliseconds(browserFresh ? m.decodeMs : null),
        backendStatus: !b ? "Ожидание данных backend" : !backendFresh ? "Данные backend устарели"
          : !b.active ? "Backend: нет потока" : "",
      });
      // Same "is a picture actually arriving right now" read the stall check above
      // uses, surfaced as the plain-language badge operators see (see render below)
      // — the numbers behind it only show in the click-opened details panel.
      setSignalLive(hasFrameCallback ? fps > 0 : w > 0 && h > 0);
    };

    const interval = setInterval(tick, 1000);
    return () => {
      cancelled = true;
      clearInterval(interval);
      if (fallbackId) clearInterval(fallbackId);
      if (rafId != null && typeof el?.cancelVideoFrameCallback === "function") {
        try {
          el.cancelVideoFrameCallback(rafId);
        } catch {
          /* ignore */
        }
      }
    };
  }, [slot, stream, requestKeyframes, cameraOn, onDecodedFrame, presentationHidden, onFramePath]);

  const headerClassName = fullscreen
    ? `${styles.header} ${styles.fullscreen}${chromeVisible ? "" : ` ${styles.chromeHidden}`}`
    : styles.header;

  return (
    <div
      className={`${styles.card}${fullscreen ? ` ${styles.fullscreen}` : ""}${fit ? ` ${styles.fit}` : ""}${presentationHidden ? ` ${styles.presentationHidden}` : ""}`}
      aria-hidden={presentationHidden || undefined}
      inert={presentationHidden}
      data-slot={slot}
      data-mount={mount}
      ref={cardRef}
    >
      <div className={headerClassName}>
        <span className={styles.title}>{title}</span>
        <div className={styles.headerEnd}>
          {cameraOn && (
            <>
              <span
                className={`${styles.signal}${signalLive ? ` ${styles.signalLive}` : ""}`}
                id={`video-stats-${slot}`}
              >
                <span className={styles.signalDot} aria-hidden="true" />
                {signalLive ? "Трансляция" : "Подключение…"}
              </span>
              <button
                type="button"
                className={`${styles.detailsToggle}${detailsOpen ? ` ${styles.open}` : ""}`}
                aria-label="Технические детали видео"
                aria-expanded={detailsOpen}
                onClick={(e) => {
                  e.stopPropagation();
                  setDetailsOpen((v) => !v);
                }}
              >
                <i className={`bi ${detailsOpen ? "bi-info-circle-fill" : "bi-info-circle"}`} />
              </button>
            </>
          )}
          {onCameraExpand && (
            <button
              type="button"
              className={styles.expandButton}
              aria-label={`${title}: на весь экран`}
              title="Развернуть эту камеру на весь экран"
              onClick={() => onCameraExpand(mount)}
            >
              <i className="bi bi-arrows-fullscreen" aria-hidden="true" />
            </button>
          )}
          <label
            className={`${styles.toggle}${cameraOn ? ` ${styles.on}` : ""}`}
            title={cameraOn ? "Выключить камеру" : "Включить камеру"}
          >
            <input
              type="checkbox"
              className={styles.toggleInput}
              data-mount={mount}
              data-slot={slot}
              checked={cameraOn}
              disabled={toggleDisabled}
              onChange={async (e) => {
                const ok = await onCameraToggle?.(mount, slot, e.target.checked);
                if (ok === false) e.target.checked = !e.target.checked;
              }}
            />
            <span className={styles.toggleTrack} aria-hidden="true">
              <span className={styles.toggleThumb}>
                <i
                  className={`bi ${cameraOn ? "bi-camera-video-fill" : "bi-camera-video-off-fill"}`}
                />
              </span>
            </span>
            <span className={styles.visuallyHidden}>
              {title}: {cameraOn ? "вкл" : "выкл"}
            </span>
          </label>
        </div>
      </div>
      <div
        className={`${styles.body}${fullscreen || fit ? ` ${styles.fullscreen}` : ""}`}
        style={fullscreen || fit ? undefined : { aspectRatio: ratio ? String(ratio) : "16 / 9" }}
      >
        <video
          ref={videoRef}
          id={`remote-video-${slot + 1}`}
          className={styles.video}
          autoPlay
          playsInline
          muted
        />
        <div
          className={`${styles.overlay}${overlayHidden ? ` ${styles.hidden}` : ""}`}
          id={`video-overlay-${slot + 1}`}
        >
          Выкл
        </div>
        {cameraOn && detailsOpen && (
          <div
            className={`${styles.diagnostics}${fullscreen ? ` ${styles.fullscreen}` : ""}`}
            role="dialog"
            aria-label={`${title}: технические детали`}
          >
            <span className={styles.diagnosticsLabel}>Разрешение</span>
            <span className={styles.diagnosticsValue}>{details.size}</span>
            <span className={styles.diagnosticsLabel}>FPS показа</span>
            <span className={styles.diagnosticsValue}>{details.fps}</span>
            <span className={styles.diagnosticsLabel}>FPS декодирования</span>
            <span className={styles.diagnosticsValue}>{details.decodedFps}</span>
            <span className={styles.diagnosticsLabel}>Битрейт</span>
            <span className={styles.diagnosticsValue}>{details.bitrate}</span>
            <span className={styles.diagnosticsLabel}>Пропущено кадров</span>
            <span className={styles.diagnosticsValue}>{details.drop}</span>
            <span className={styles.diagnosticsLabel}>Очередь backend</span>
            <span className={styles.diagnosticsValue}>{details.queue}</span>
            <span className={styles.diagnosticsLabel} title="Время самого старого элемента в очереди Python после чтения RTSP">Возраст в очереди</span>
            <span className={styles.diagnosticsValue}>{details.oldestWait}</span>
            <span className={styles.diagnosticsLabel} title="Среднее и максимальное время ожидания в очереди backend за последнюю секунду">Ожидание ср. / макс.</span>
            <span className={styles.diagnosticsValue}>{details.queueWait}</span>
            <span className={styles.diagnosticsLabel} title="Среднее время в буфере WebRTC за последний интервал измерения">Буфер браузера</span>
            <span className={styles.diagnosticsValue}>{details.jitterBuffer}</span>
            <span className={styles.diagnosticsLabel} title="Среднее время декодирования одного кадра за последний интервал измерения">Декодирование</span>
            <span className={styles.diagnosticsValue}>{details.decode}</span>
            <span className={styles.diagnosticsNote}>
              {details.backendStatus && <>{details.backendStatus}. </>}
              Это части задержки, а не полное время от камеры до экрана. «—» означает, что замер недоступен.
            </span>
            <VideoLatencyPanel slot={slot} camera={title} metrics={metrics}
              setLatencyMode={setLatencyMode} startLatencyComparison={startLatencyComparison}
              cancelLatencyComparison={cancelLatencyComparison} />
            <VideoFramePathPanel camera={title} report={framePath.report} exportReport={framePath.exportReport} start={()=>{requestFrameTiming?.(mount);framePath.start();}}
              stop={framePath.stop} supported={typeof videoRef.current?.requestVideoFrameCallback === "function"} />
          </div>
        )}
      </div>
    </div>
  );
}

export interface VideoGridProps extends VideoLatencyActions {
  /** `channel` is the camera's network-assigned slot (see `CameraCatalogEntry.channel`); it doubles as the index into `streams`/`metrics`. */
  cameras: { mount: string; label: string; channel: number; enabled: boolean }[];
  streams: Record<number, MediaStream | null>;
  metrics: Record<number, SlotMetrics>;
  backendVideoStats?: Record<string, CameraVideoStats>;
  backendClock?: VideoClockEstimate | null;
  requestFrameTiming?: (mount: string) => void;
  readFrameTiming?: (mount: string) => CameraFrameTiming | undefined;
  cameraEnabled: boolean[];
  status: string;
  onCameraToggle?: CameraToggleHandler;
  toggleDisabled?: boolean;
  onCameraExpand?: (mount: string) => void;
  requestKeyframes?: (slot: number) => void;
  onDecodedFrame?: (slot: number) => void;
  restoreSlot?: (slot: number) => boolean;
  /** ViewerPage's fullscreen layout — every card fills its grid cell edge-to-edge
   * instead of sizing to its own aspect ratio, and the plain-text `StatusBar` row is
   * dropped (ViewerPage shows status in its own floating chip instead, since a static
   * row has nowhere good to sit once the video fills the whole screen). */
  fullscreen?: boolean;
  /** Fullscreen only — fades each card's floating header out after a run of
   * inactivity (see ViewerPage's auto-hide timer). Ignored while `fullscreen` is false. */
  chromeVisible?: boolean;
  /** Docked layout that must not scroll: the grid fills the height its parent gives it
   * and every picture is letterboxed (object-fit: contain) into its cell, keeping the
   * card header and status row. The parent must give the grid a definite height. */
  fit?: boolean;
  /** Expand one slot visually while keeping all streams and monitor checks running. */
  focusedSlot?: number;
}

export function VideoGrid({
  cameras,
  streams,
  metrics,
  backendVideoStats,
  backendClock,
  requestFrameTiming,
  readFrameTiming,
  cameraEnabled,
  status,
  onCameraToggle,
  toggleDisabled,
  onCameraExpand,
  requestKeyframes,
  onDecodedFrame,
  restoreSlot,
  setLatencyMode,
  startLatencyComparison,
  cancelLatencyComparison,
  fullscreen = false,
  chromeVisible = true,
  fit = false,
  focusedSlot,
}: VideoGridProps) {
  const list = useMemo(
    () => (cameras?.length > 0 ? [...cameras].sort((a, b) => a.channel - b.channel) : []),
    [cameras]
  );

  const hasFocusedCamera = fullscreen && list.some((cam) => cam.channel === focusedSlot);

  // Tab-focus resync: rebuild streams after background-tab decode throttling.
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    let wasHidden = document.hidden;
    const onVisibility = () => {
      if (document.hidden) {
        wasHidden = true;
        return;
      }
      if (!wasHidden) return;
      wasHidden = false;
      for (const cam of list) {
        restoreSlot?.(cam.channel);
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [list, restoreSlot]);

  return (
    <section
      className={`${styles.panel}${fullscreen ? ` ${styles.fullscreen}` : ""}${fit ? ` ${styles.fit}` : ""}`}
    >
      <div
        className={`${styles.grid}${fullscreen ? ` ${styles.fullscreen}` : ""}${fit ? ` ${styles.fit}` : ""}${
          hasFocusedCamera || list.length === 1
            ? ` ${styles.single}`
            : list.length >= 2
              ? ` ${styles.twoUp}`
              : ""
        }`}
      >
        {list.length === 0 ? (
          <div className={styles.placeholder}>Ожидание каталога камер (robot-config)…</div>
        ) : (
          list.map((cam) => {
            const slot = cam.channel;
            const on = Boolean(cameraEnabled[slot]);
            return (
              <VideoCard
                key={`${cam.mount}-${slot}`}
                slot={slot}
                mount={cam.mount}
                title={cam.label || `Камера ${slot + 1} (${cam.mount})`}
                stream={streams[slot] ?? null}
                metrics={metrics[slot]}
                backendStats={backendVideoStats?.[cam.mount]}
                backendClock={backendClock}
                requestFrameTiming={requestFrameTiming}
                readFrameTiming={readFrameTiming}
                overlayHidden={on}
                cameraOn={on}
                onCameraToggle={onCameraToggle}
                toggleDisabled={toggleDisabled}
                onCameraExpand={onCameraExpand}
                requestKeyframes={requestKeyframes}
                onDecodedFrame={onDecodedFrame}
                setLatencyMode={setLatencyMode}
                startLatencyComparison={startLatencyComparison}
                cancelLatencyComparison={cancelLatencyComparison}
                fullscreen={fullscreen}
                chromeVisible={chromeVisible}
                fit={fit}
                presentationHidden={hasFocusedCamera && slot !== focusedSlot}
              />
            );
          })
        )}
      </div>
      {/* Fullscreen drops the plain-text status row — ViewerPage shows the same status
          in its own floating chip instead (see .fullscreenStatusChip), which fades
          with the rest of the chrome rather than sitting fixed under the video. */}
      {!fullscreen && <StatusBar status={status} />}
    </section>
  );
}
